import React, { useState } from 'react'

export interface ThemeColors {
    background: string
    cardBackground: string
    cardText: string
    buttonBackground: string
    buttonText: string
    headerBackground: string
    headerText: string
}

export const BRAND_THEMES: Record<string, ThemeColors> = {
    'brand-red': {
        background: '#D31145',
        cardBackground: '#D31145',
        cardText: 'white',
        buttonBackground: '#D31145',
        buttonText: 'white',
        headerBackground: '#D31145',
        headerText: 'white'
    },
    'white': {
        background: 'white',
        cardBackground: 'white',
        cardText: 'black',
        buttonBackground: 'white',
        buttonText: 'black',
        headerBackground: 'white',
        headerText: 'black'
    },
    'grey': {
        background: '#808080',
        cardBackground: '#808080',
        cardText: 'white',
        buttonBackground: '#808080',
        buttonText: 'white',
        headerBackground: '#808080',
        headerText: 'white'
    },
    'dark-grey': {
        background: '#404040',
        cardBackground: '#404040',
        cardText: 'white',
        buttonBackground: '#404040',
        buttonText: 'white',
        headerBackground: '#404040',
        headerText: 'white'
    },
    'black': {
        background: 'black',
        cardBackground: 'black',
        cardText: 'white',
        buttonBackground: 'black',
        buttonText: 'white',
        headerBackground: 'black',
        headerText: 'white'
    },
    'light-grey': {
        background: '#D3D3D3',
        cardBackground: '#D3D3D3',
        cardText: 'black',
        buttonBackground: '#D3D3D3',
        buttonText: 'black',
        headerBackground: '#D3D3D3',
        headerText: 'black'
    }
}

interface ThemeToggleProps {
    currentTheme: string
    onThemeChange: (themeName: string) => void
    onBackgroundColorChange: (color: string) => void
    onHeaderColorChange: (color: string) => void
    onCardColorChange: (color: string) => void
    onFontColorChange: (color: string) => void
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
    currentTheme,
    onThemeChange,
    onBackgroundColorChange,
    onHeaderColorChange,
    onCardColorChange,
    onFontColorChange
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const colorOptions = [
        { name: 'Brand Red', value: '#D31145', key: 'brand-red' },
        { name: 'White', value: 'white', key: 'white' },
        { name: 'Grey', value: '#808080', key: 'grey' },
        { name: 'Dark Grey', value: '#404040', key: 'dark-grey' },
        { name: 'Black', value: 'black', key: 'black' },
        { name: 'Light Grey', value: '#D3D3D3', key: 'light-grey' }
    ]

    const fontColors = [
        { name: 'White', value: 'white' },
        { name: 'Black', value: 'black' },
        { name: 'Brand Red', value: '#D31145' },
        { name: 'Grey', value: '#808080' }
    ]

    return (
        <div className="fixed bottom-6 right-6 z-50">
            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="bg-white text-black p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-300"
                title="Theme Testing Panel"
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a4 4 0 004-4V5z" />
                </svg>
            </button>

            {/* Theme Panel */}
            {isOpen && (
                <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-2xl border border-gray-300 p-4 w-80">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-black mb-2">🎨 Theme Testing Panel</h3>
                        <p className="text-sm text-gray-600 mb-4">Test different color combinations for your UI</p>
                    </div>

                    {/* Full Theme Presets */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-black mb-2">Complete Theme Presets:</h4>
                        <div className="grid grid-cols-2 gap-2">
                            {colorOptions.map((color) => (
                                <button
                                    key={color.key}
                                    onClick={() => onThemeChange(color.key)}
                                    className={`p-2 rounded text-xs font-medium transition-all duration-200 border-2 ${
                                        currentTheme === color.key 
                                            ? 'border-blue-500 shadow-md' 
                                            : 'border-gray-200 hover:border-gray-400'
                                    }`}
                                    style={{ 
                                        backgroundColor: color.value,
                                        color: color.value === 'white' || color.value === '#D3D3D3' ? 'black' : 'white'
                                    }}
                                >
                                    {color.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Individual Background Color Testing */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-black mb-2">Background Colors:</h4>
                        <div className="grid grid-cols-3 gap-2">
                            {colorOptions.map((color) => (
                                <button
                                    key={`bg-${color.key}`}
                                    onClick={() => onBackgroundColorChange(color.value)}
                                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500 transition-all duration-200"
                                    style={{ backgroundColor: color.value }}
                                    title={`Background: ${color.name}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Individual Header Color Testing */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-black mb-2">Header Colors:</h4>
                        <div className="grid grid-cols-3 gap-2">
                            {colorOptions.map((color) => (
                                <button
                                    key={`header-${color.key}`}
                                    onClick={() => onHeaderColorChange(color.value)}
                                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500 transition-all duration-200"
                                    style={{ backgroundColor: color.value }}
                                    title={`Header: ${color.name}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Individual Card Color Testing */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-black mb-2">Card Colors:</h4>
                        <div className="grid grid-cols-3 gap-2">
                            {colorOptions.map((color) => (
                                <button
                                    key={`card-${color.key}`}
                                    onClick={() => onCardColorChange(color.value)}
                                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500 transition-all duration-200"
                                    style={{ backgroundColor: color.value }}
                                    title={`Card: ${color.name}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Individual Font Color Testing */}
                    <div className="mb-4">
                        <h4 className="text-sm font-semibold text-black mb-2">Font Colors:</h4>
                        <div className="grid grid-cols-2 gap-2">
                            {fontColors.map((color) => (
                                <button
                                    key={`font-${color.value}`}
                                    onClick={() => onFontColorChange(color.value)}
                                    className="px-3 py-1 rounded text-sm border border-gray-300 hover:border-gray-500 transition-all duration-200"
                                    style={{ 
                                        backgroundColor: 'white',
                                        color: color.value
                                    }}
                                >
                                    {color.name} Text
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Current Theme Display */}
                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                        Current: {colorOptions.find(c => c.key === currentTheme)?.name || 'Custom'}
                    </div>
                </div>
            )}
        </div>
    )
}

// Wrapper component that integrates with ThemeContext
import { useTheme } from '../contexts/ThemeContext'

const ThemeToggleWrapper: React.FC = () => {
    const { 
        currentTheme, 
        setTheme, 
        setCustomBackgroundColor, 
        setCustomHeaderColor, 
        setCustomCardColor, 
        setCustomFontColor 
    } = useTheme()

    return (
        <ThemeToggle
            currentTheme={currentTheme}
            onThemeChange={setTheme}
            onBackgroundColorChange={setCustomBackgroundColor}
            onHeaderColorChange={setCustomHeaderColor}
            onCardColorChange={setCustomCardColor}
            onFontColorChange={setCustomFontColor}
        />
    )
}

export default ThemeToggleWrapper